"use client"

import { AUTH_TOKEN } from "constants/AuthConstant"
import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { setExpensesState } from "store/slices/expenses"
import { isAuth } from "./isAuth"
import { setAuthUser, signOutSuccess } from "store/slices/authSlice"
import { refreshAccessToken, isRefreshingToken } from "refreshToken"

const useExpenses = () => {
  const [expenses, setExpenses] = useState([])
  const [expense, setExpense] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState(null)

  const base_url = process.env.REACT_APP_API_URL;

  const url_getAllExpenses = base_url + "/expenses";
  const url_totalExpenses = base_url + "/expenses/total";

  const dispatch = useDispatch()
  const tokenFromState = useSelector((state) => state.auth.token)

  // Vérifie la cohérence du token entre le state et le localStorage
  if (localStorage.getItem(AUTH_TOKEN) !== tokenFromState) {
    localStorage.removeItem(AUTH_TOKEN)
    dispatch(signOutSuccess())
    dispatch(setAuthUser({}))
  }

  // Fonction générique pour les appels API avec gestion du token
  const authFetch = async (url, options = {}, navigate) => {
    let token = tokenFromState || localStorage.getItem(AUTH_TOKEN)

    // Si pas de token et pas de refresh en cours, on tente un refresh
    if (!token && !isRefreshingToken()) {
      token = await refreshAccessToken()
    }

    if (!token) {
      throw new Error("Authentication required")
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })

    // Si token expiré, on tente un refresh
    if (response.status === 401) {
      token = await refreshAccessToken()
      if (!token) {
        localStorage.removeItem(AUTH_TOKEN)
        dispatch(signOutSuccess())
        dispatch(setAuthUser({}))
        if (navigate) navigate("/login")
        throw new Error("Session expired")
      }

      // On réessaye avec le nouveau token
      return fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
    }

    return response
  }

  const getAllExpenses = async (navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(url_getAllExpenses, { method: "GET" }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      const data = await response.json()
      setExpenses(data.data || [])
      dispatch(setExpensesState(data.data || []))
    } catch (err) {
      console.error("Failed to get expenses", err)
      setError(err.message || "Failed to get expenses")
    } finally {
      setIsLoading(false)
    }
  }

  const addExpense = async (data, navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(
        url_getAllExpenses,
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        navigate,
      )

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      const responseData = await response.json()
      setExpense(responseData)
      await getAllExpenses(navigate)
    } catch (err) {
      console.error("Failed to add expense", err)
      setError(err.message || "Failed to add expense")
    } finally {
      setIsLoading(false)
    }
  }

  const totalExpenses = async (navigate) => {
    setIsLoading(true)
    setError(null)
    setTotal(0)

    try {
      const response = await authFetch(url_totalExpenses, { method: "GET" }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      const responseData = await response.json()
      setTotal(responseData.total)
    } catch (err) {
      console.error("Failed to get total expenses", err)
      setError(err.message || "Failed to get total expenses")
    } finally {
      setIsLoading(false)
    }
  }

  const getExpenseById = async (id, navigate) => {
    if (!id) return null
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(`${url_getAllExpenses}/${id}`, { method: "GET" }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return null
      }

      const data = await response.json()
      setExpense(data)
      return data
    } catch (err) {
      console.error("Failed to get expense", err)
      setError(err.message || "Failed to get expense")
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const updateExpense = async (data, id, navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(
        `${url_getAllExpenses}/${id}`,
        {
          method: "PUT",
          body: JSON.stringify(data),
        },
        navigate,
      )

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      const responseData = await response.json()
      setExpense(responseData)
      await getAllExpenses(navigate)
      return responseData
    } catch (err) {
      console.error("Failed to update expense", err)
      setError(err.message || "Failed to update expense")
    } finally {
      setIsLoading(false)
    }
  }

  const deleteExpense = async (id, navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(`${url_getAllExpenses}/${id}`, { method: "DELETE" }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      setExpense([])
      await getAllExpenses(navigate)
    } catch (err) {
      console.error("Failed to delete expense", err)
      setError(err.message || "Failed to delete expense")
    } finally {
      setIsLoading(false)
    }
  }

  const getExpensesByTypeDepense = async (q, navigate) => {
    if (!q) return
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(`${url_getAllExpenses}/type?id=${q}`, { method: "GET" }, navigate)

      if (!response.ok) {
        const errorData = await response.json()

        // Cas spécial pour 404 "No Expenses found"
        if (errorData?.statusCode === 404 && errorData?.message === "No Expenses found.") {
          setExpenses(null)
          dispatch(setExpensesState(null))
          return
        }

        handleApiError(errorData)
        return
      }

      const data = await response.json()
      setExpenses(data || [])
      dispatch(setExpensesState(data || []))
    } catch (err) {
      console.error("Failed to get expenses by type", err)
      setError(err.message || "Failed to get expenses by type")
    } finally {
      setIsLoading(false)
    }
  }

  // Gestion centralisée des erreurs API
  const handleApiError = (errorData) => {
    if (!isAuth(errorData)) {
      localStorage.removeItem(AUTH_TOKEN)
      dispatch(signOutSuccess())
      dispatch(setAuthUser({}))
    }

    if (errorData?.statusCode === 500) {
      setError("Erreur interne du serveur")
      return
    }

    if (Array.isArray(errorData?.data?.message)) {
      setError(errorData.data.message[0])
      return
    }

    if (errorData?.data?.message) {
      setError(errorData.data.message)
      return
    }

    if (errorData?.message) {
      setError(errorData.message)
      return
    }

    setError("Erreur inconnue")
  }

  return {
    getExpensesByTypeDepense,
    deleteExpense,
    updateExpense,
    getExpenseById,
    addExpense,
    getAllExpenses,
    expense,
    setExpense,
    expenses,
    setExpenses,
    isLoading,
    error,
    setError,
    total,
    totalExpenses,
  }
}

export default useExpenses